import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeft, Zap, Leaf, Sprout, Gem, Gift, Users } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { injectPackStyles } from './packStyles';

const PACK_PRICE = 50;
const CONFETTI_ALPHA_SCALE = 0.86;

/** Poids alignes sur le backend (affichage uniquement) */
const PACK_LOOT_TABLE = [
  { id: 'coins_10', weight: 145, label: '10 Global Coins', sub: 'Credites a la recuperation.', rarity: 'common' },
  { id: 'priority', weight: 135, label: 'Livraison prioritaire', sub: 'Prochaine commande en priorite.', rarity: 'common' },
  { id: 'dry_1g', weight: 125, label: '1G Drytech x Goldenfarm', sub: 'Sur ta prochaine commande dry.', rarity: 'common' },
  { id: 'pass_argent_1w', weight: 95, label: 'Pass Argent — 1 semaine', sub: 'Active a la recuperation.', rarity: 'rare' },
  { id: 'mousse_2g', weight: 90, label: '2G de mousse', sub: 'Sur ta prochaine commande mousse.', rarity: 'rare' },
  { id: 'coins_20', weight: 70, label: '20 Global Coins', sub: 'Credites a la recuperation.', rarity: 'rare' },
  { id: 'sahas_static_1g', weight: 48, label: '1G Sahas Static', sub: 'Sur ta prochaine commande Sahas Static.', rarity: 'epic' },
  { id: 'coins_50', weight: 28, label: '50 Global Coins', sub: 'Credites a la recuperation.', rarity: 'epic' },
  { id: 'pass_or_1w', weight: 22, label: 'Pass Or — 1 semaine', sub: 'Active a la recuperation.', rarity: 'epic' },
];

const PACK_LOOT_BY_ID = Object.fromEntries(PACK_LOOT_TABLE.map((r) => [r.id, r]));

const PACK_HOME_BG = '/emoji/SWISS3.png';

const CoinImg = ({ size = 28 }) => (
  <img src="/emoji/coin1.png" alt="" width={size} height={size} style={{ objectFit: 'contain' }} decoding="async" onError={(e) => { e.target.style.display = 'none'; }} />
);

function PackSwissBackground({ overlay = false }) {
  return (
    <div
      className={'pack-swiss-bg' + (overlay ? ' pack-swiss-bg--overlay' : '')}
      aria-hidden="true"
    >
      <img className="pack-swiss-bg__img" src={PACK_HOME_BG} alt="" decoding="async" />
      <div className="pack-swiss-bg__veil" />
    </div>
  );
}

function getPackPrizeMeta(prizeId) {
  return PACK_LOOT_BY_ID[prizeId] || PACK_LOOT_TABLE[0];
}

/** Icones fiables (pas d'emoji UTF-8 casses dans WebView Telegram) */
function PackPrizeIcon({ prizeId, size = 88, className = '' }) {
  const box = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: size,
    height: size,
  };
  if (prizeId.startsWith('coins')) {
    return (
      <img
        className={className || 'pr-reveal-img'}
        src="/emoji/coin1.png"
        alt=""
        width={size}
        height={size}
        style={{ objectFit: 'contain' }}
        decoding="async"
      />
    );
  }
  if (prizeId === 'pass_or_1w') {
    return (
      <img
        className={className || 'pr-reveal-img'}
        src="/emoji/or1.webp"
        alt=""
        width={size}
        height={size}
        style={{ objectFit: 'contain' }}
        decoding="async"
      />
    );
  }
  if (prizeId === 'pass_argent_1w') {
    return (
      <img
        className={className || 'pr-reveal-img'}
        src="/emoji/silver1.webp"
        alt=""
        width={size}
        height={size}
        style={{ objectFit: 'contain' }}
        decoding="async"
      />
    );
  }
  if (prizeId === 'priority') {
    return (
      <div style={box} className={className} aria-hidden="true">
        <Zap size={Math.round(size * 0.52)} strokeWidth={2.25} color="#fde047" fill="#facc15" />
      </div>
    );
  }
  if (prizeId === 'mousse_2g') {
    return (
      <div style={box} className={className} aria-hidden="true">
        <Leaf size={Math.round(size * 0.48)} strokeWidth={2} color="#86efac" />
      </div>
    );
  }
  if (prizeId === 'dry_1g') {
    return (
      <div style={box} className={className} aria-hidden="true">
        <Sprout size={Math.round(size * 0.48)} strokeWidth={2} color="#a3e635" />
      </div>
    );
  }
  if (prizeId === 'sahas_static_1g') {
    return (
      <div style={box} className={className} aria-hidden="true">
        <Gem size={Math.round(size * 0.48)} strokeWidth={2} color="#fbbf24" />
      </div>
    );
  }
  return null;
}

function packConfettiPalette(prize) {
  if (!prize) return ['#4a90e2', '#6ba3e8', '#93c5fd', '#bfdbfe', '#ffffff', '#dbeafe'];
  if (prize.rarity === 'epic') {
    return ['#ffffff', '#f0f9ff', '#4a90e2', '#93c5fd', '#6ba3e8', '#bfdbfe', '#dbeafe'];
  }
  if (prize.rarity === 'rare') {
    return ['#4a90e2', '#22c55e', '#6ba3e8', '#86efac', '#93c5fd', '#ffffff'];
  }
  return ['#4a90e2', '#3b78c8', '#6ba3e8', '#93c5fd', '#bfdbfe', '#e2e8f0'];
}

function revealCardClassName(prize) {
  let cls = 'pack-reveal-card pack-reveal-booster';
  if (prize?.rarity === 'epic') cls += ' r-god';
  return cls;
}

function getPackCreditNotice(prize) {
  if (!prize) return { title: 'Lot credite', subtitle: '' };
  if (prize.id === 'coins_50') return { title: '+50 Global Coins', subtitle: 'Solde mis a jour.' };
  if (prize.id === 'coins_20') return { title: '+20 Global Coins', subtitle: 'Solde mis a jour.' };
  if (prize.id === 'coins_10') return { title: '+10 Global Coins', subtitle: 'Solde mis a jour.' };
  if (prize.id === 'mousse_2g') return { title: '+2G de mousse', subtitle: 'Sur ta prochaine commande mousse.' };
  if (prize.id === 'dry_1g') return { title: '+1G Drytech x Goldenfarm', subtitle: 'Sur ta prochaine commande dry.' };
  if (prize.id === 'sahas_static_1g') return { title: '+1G Sahas Static', subtitle: 'Sur ta prochaine commande Sahas Static.' };
  if (prize.id === 'priority') return { title: 'Livraison prioritaire', subtitle: 'Prochaine commande en priorite.' };
  if (prize.id === 'pass_argent_1w') return { title: 'Pass Argent — 1 semaine', subtitle: 'Pass active sur ton compte.' };
  if (prize.id === 'pass_or_1w') return { title: 'Pass Or — 1 semaine', subtitle: 'Pass active sur ton compte.' };
  return { title: prize.label, subtitle: 'Recompense enregistree.' };
}

function PackCreditGlass({ prize, visible }) {
  if (!prize || !visible) return null;
  const notice = getPackCreditNotice(prize);
  return (
    <div
      className={'pack-credit-glass' + (visible ? ' pack-credit-glass--show' : '')}
      role="status"
      aria-live="polite"
    >
      <div className="pack-credit-glass__shine" aria-hidden="true" />
      <div className="pack-credit-glass__inner">
        <div className="pack-credit-glass__icon" aria-hidden="true">
          <PackPrizeIcon prizeId={prize.id} size={40} />
        </div>
        <div className="pack-credit-glass__badge">Credite</div>
        <div className="pack-credit-glass__ttl">{notice.title}</div>
        <p className="pack-credit-glass__sub">{notice.subtitle}</p>
      </div>
    </div>
  );
}

function RevealCard({ prize }) {
  if (!prize) return null;
  const rar = prize.rarity === 'epic' ? 'epic' : prize.rarity === 'rare' ? 'rare' : 'common';
  const rarLabel = prize.rarity === 'epic' ? 'EPIQUE' : prize.rarity === 'rare' ? 'RARE' : 'STANDARD';
  return (
    <div className="pack-card-flip-inner">
      <div className="pr-card-edge" aria-hidden="true" />
      <div className="pr-card-back" aria-hidden="true">
        <div className="pr-pack-back-inner">
          <div className="pr-trophy-wrap pr-trophy-wrap--back">
            <img className="pack-trophy-img" src="/emoji/trophy1.png" alt="" width="256" height="256" decoding="async" />
          </div>
          <div className="pr-pack-back-ttl">GLOBAL<br />PACK</div>
          <div className="pr-pack-back-sub" />
        </div>
      </div>
      <div className="pr-card-front">
        <div className="pr-violet-face">
          <div className="pr-violet-shine" aria-hidden="true" />
          <div className="pr-trophy-wrap pr-trophy-wrap--reveal">
            <PackPrizeIcon prizeId={prize.id} size={112} className="pr-reveal-img" />
          </div>
          <div className={`pr-rarity ${rar}`}>{rarLabel}</div>
          <div className="pr-violet-ttl">{prize.label}</div>
          <div className="pr-violet-desc">{prize.sub}</div>
        </div>
      </div>
    </div>
  );
}

function PackReferralBanner({ freePacks, onGoReferral }) {
  const hasFree = freePacks > 0;
  return (
    <div className={'global-pack-referral' + (hasFree ? ' global-pack-referral--active' : '')}>
      <div className="global-pack-referral__shine" aria-hidden="true" />
      <div className="global-pack-referral__inner global-pack-referral__inner--compact">
        <div className="global-pack-referral__row">
          <div className="global-pack-referral__icon" aria-hidden="true">
            {hasFree ? <Gift size={22} color="#86efac" /> : <Users size={22} color="#93c5fd" />}
          </div>
          <div className="global-pack-referral__body">
            <p className="global-pack-referral__ttl">
              {hasFree
                ? `${freePacks} Global Pack gratuit${freePacks > 1 ? 's' : ''} — bouton OUVRIR`
                : 'Parrainez un ami = 1 Pack offert'}
            </p>
            <p className="global-pack-referral__sub">
              {hasFree
                ? 'Livraison instantanée'
                : ''}
            </p>
          </div>
        </div>
        {!hasFree && onGoReferral && (
          <button type="button" className="gs-pack-btn gs-pack-btn--primary global-pack-referral__cta" onClick={onGoReferral}>
            Parrainer
          </button>
        )}
      </div>
    </div>
  );
}

function PackBoosterFace() {
  return (
    <div className="pack-face pack-front">
      <div className="pack-crimps pack-crimps--top" aria-hidden="true" />
      <div className="pack-body-core">
        <div className="pack-crest pack-crest--overlay" aria-hidden="true">
          <div className="pack-trophy-frame">
            <img className="pack-trophy-img" src="/emoji/trophy1.png" alt="" width="256" height="256" decoding="async" />
          </div>
        </div>
        <div className="pack-booster-lower">
          <div className="pack-ttl">GLOBAL<br />PACK</div>
          <div className="pack-subt" />
        </div>
      </div>
      <div className="pack-crimps pack-crimps--bottom" aria-hidden="true" />
    </div>
  );
}

export default function PackFeature({
  onBack,
  onGoReferral,
  globalCoins,
  setGlobalCoins,
  freePacks,
  setFreePacks,
  setGameBonus2g,
  setPackPriorityBonus,
  setWheelBonuses,
  setUserPass,
  setPassExpiryDate,
  telegramChatId,
  BACKEND_URL,
  fetchWithTimeout,
}) {
  const [packOpening, setPackOpening] = useState(false);
  const [ovOpen, setOvOpen] = useState(false);
  const [ovBtnShow, setOvBtnShow] = useState(false);
  const [showCreditGlass, setShowCreditGlass] = useState(false);
  const [revealPrize, setRevealPrize] = useState(null);
  const [claiming, setClaiming] = useState(false);

  const packOvRef = useRef(null);
  const packAnimBoxRef = useRef(null);
  const packRevealCardRef = useRef(null);
  const packAnimStackRef = useRef(null);
  const packCardSlotRef = useRef(null);
  const packOvParticlesRef = useRef(null);
  const packConfettiCanvasRef = useRef(null);
  const packOpenTimersRef = useRef([]);
  const packConfettiRafRef = useRef(null);
  const packRevealDoneRef = useRef(false);
  const pendingPrizeIdRef = useRef(null);

  useEffect(() => {
    injectPackStyles();
  }, []);

  const packAfter = useCallback((fn, ms) => {
    const id = setTimeout(fn, ms);
    packOpenTimersRef.current.push(id);
    return id;
  }, []);

  const clearPackOpenTimers = useCallback(() => {
    packOpenTimersRef.current.forEach(clearTimeout);
    packOpenTimersRef.current = [];
  }, []);

  const stopPackConfetti = useCallback(() => {
    if (packConfettiRafRef.current) {
      cancelAnimationFrame(packConfettiRafRef.current);
      packConfettiRafRef.current = null;
    }
    const canvas = packConfettiCanvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      if (ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);
    }
  }, []);

  const firePackConfettiBurst = useCallback((prize) => {
    const canvas = packConfettiCanvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width;
    canvas.height = rect.height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const palette = packConfettiPalette(prize);
    const particles = [];
    const cx = canvas.width * 0.5;
    const cy = canvas.height * 0.42;
    for (let i = 0; i < 72; i++) {
      const ang = Math.random() * Math.PI * 2;
      const spd = 2 + Math.random() * 7;
      particles.push({
        x: cx,
        y: cy,
        vx: Math.cos(ang) * spd,
        vy: Math.sin(ang) * spd - 2,
        w: 4 + Math.random() * 5,
        h: 3 + Math.random() * 4,
        color: palette[i % palette.length],
        rot: Math.random() * Math.PI,
        vr: (Math.random() - 0.5) * 0.2,
        shape: Math.random() > 0.5 ? 1 : 0,
        life: 1,
      });
    }
    const t0 = performance.now();
    const step = (now) => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      let alive = 0;
      for (const p of particles) {
        p.x += p.vx;
        p.y += p.vy;
        p.vy += 0.12;
        p.rot += p.vr;
        p.life -= 0.012;
        if (p.life > 0) {
          ctx.globalAlpha = Math.max(0, p.life) * CONFETTI_ALPHA_SCALE;
          ctx.save();
          ctx.translate(p.x, p.y);
          ctx.rotate(p.rot);
          ctx.fillStyle = p.color;
          if (p.shape === 1) {
            ctx.beginPath();
            ctx.arc(0, 0, p.w * 0.55, 0, Math.PI * 2);
            ctx.fill();
          } else {
            ctx.fillRect(-p.w * 0.5, -p.h * 0.5, p.w, p.h);
          }
          ctx.restore();
          ctx.globalAlpha = 1;
          alive++;
        }
      }
      if (alive > 0 && now - t0 < 5400) packConfettiRafRef.current = requestAnimationFrame(step);
      else stopPackConfetti();
    };
    packConfettiRafRef.current = requestAnimationFrame(step);
  }, [stopPackConfetti]);

  const resetPackTopLip = useCallback((box) => {
    if (!box) return;
    const lip = box.querySelector('.pack-crimps--top');
    if (!lip) return;
    lip.style.animation = 'none';
    lip.style.clipPath = '';
    lip.style.opacity = '';
    lip.style.transform = '';
    lip.style.filter = '';
    void lip.offsetWidth;
    lip.style.animation = '';
  }, []);

  const applyClaimPayload = useCallback((data) => {
    if (data.globalCoins !== undefined) setGlobalCoins(data.globalCoins);
    if (data.freePacks !== undefined) setFreePacks(data.freePacks);
    if (data.gameBonus2g !== undefined) setGameBonus2g(data.gameBonus2g);
    if (data.packPriorityBonus !== undefined) setPackPriorityBonus(data.packPriorityBonus);
    if (data.wheelBonuses) setWheelBonuses(data.wheelBonuses);
    if (data.pass) setUserPass(data.pass);
    if (data.passExpiryDate !== undefined) setPassExpiryDate(data.passExpiryDate);
  }, [setGlobalCoins, setFreePacks, setGameBonus2g, setPackPriorityBonus, setWheelBonuses, setUserPass, setPassExpiryDate]);

  const openPackRemote = useCallback(async (usedFreePack) => {
    if (!telegramChatId || !BACKEND_URL) {
      toast.error('Connexion requise pour ouvrir le Global Pack');
      return null;
    }
    try {
      const res = await fetchWithTimeout(`${BACKEND_URL}/client/pack/open`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ chatId: telegramChatId, usedFreePack: !!usedFreePack }),
      });
      const data = await res.json();
      if (data.success && data.prizeId) {
        if (data.globalCoins !== undefined) setGlobalCoins(data.globalCoins);
        if (data.freePacks !== undefined) setFreePacks(data.freePacks);
        return data.prizeId;
      }
      toast.error(data.message || 'Erreur lors de l\'ouverture du pack');
      return null;
    } catch (e) {
      console.error('pack/open', e);
      toast.error('Connexion impossible');
      return null;
    }
  }, [telegramChatId, BACKEND_URL, fetchWithTimeout, setGlobalCoins, setFreePacks]);

  const claimPackRemote = useCallback(async () => {
    if (!telegramChatId || !BACKEND_URL) {
      toast.error('Connexion requise');
      return false;
    }
    try {
      const res = await fetchWithTimeout(`${BACKEND_URL}/client/pack/claim`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ chatId: telegramChatId }),
      });
      const data = await res.json();
      if (data.success) {
        applyClaimPayload(data);
        return true;
      }
      toast.error(data.message || 'Erreur lors de la recuperation du lot');
      return false;
    } catch (e) {
      console.error('pack/claim', e);
      toast.error('Connexion impossible');
      return false;
    }
  }, [telegramChatId, BACKEND_URL, fetchWithTimeout, applyClaimPayload]);

  const resetPackOverlay = useCallback(() => {
    clearPackOpenTimers();
    setOvOpen(false);
    setOvBtnShow(false);
    setShowCreditGlass(false);
    setRevealPrize(null);
    setPackOpening(false);
    setClaiming(false);
    pendingPrizeIdRef.current = null;
    packRevealDoneRef.current = false;
    const box = packAnimBoxRef.current;
    const card = packRevealCardRef.current;
    const stack = packAnimStackRef.current;
    const slot = packCardSlotRef.current;
    if (slot) slot.classList.remove('slot-released');
    if (stack) stack.classList.remove('pack-card-forward');
    if (box) {
      box.classList.remove('phase-tear-top', 'phase-rip-open', 'phase-tear', 'pack-shake-go');
      resetPackTopLip(box);
    }
    if (card) {
      card.style.display = 'none';
      card.classList.remove('reveal-play', 'reveal-in-front', 'pack-card-show-front', 'pack-card-do-settle');
    }
    if (packOvParticlesRef.current) packOvParticlesRef.current.innerHTML = '';
    stopPackConfetti();
  }, [clearPackOpenTimers, resetPackTopLip, stopPackConfetti]);

  const spawnPackParticles = useCallback(() => {
    const box = packOvParticlesRef.current;
    if (!box) return;
    box.innerHTML = '';
    for (let i = 0; i < 32; i++) {
      const dot = document.createElement('div');
      dot.className = 'pov-dot';
      const ang = (i / 32) * Math.PI * 2 + Math.random() * 0.4;
      const dist = 70 + Math.random() * 130;
      dot.style.setProperty('--dx', `${Math.round(Math.cos(ang) * dist)}px`);
      dot.style.setProperty('--dy', `${Math.round(Math.sin(ang) * dist)}px`);
      dot.style.animationDelay = `${Math.random() * 0.15}s`;
      const hue = i % 3 === 0 ? '74,144,226' : i % 3 === 1 ? '147,197,253' : '191,219,254';
      dot.style.background = `rgba(${hue},0.78)`;
      dot.style.boxShadow = `0 0 10px rgba(${hue},0.48)`;
      box.appendChild(dot);
    }
  }, []);

  const closePackOv = useCallback(async () => {
    if (claiming) return;
    setClaiming(true);
    const prize = revealPrize;
    const ok = await claimPackRemote();
    if (!ok) {
      setClaiming(false);
      return;
    }
    setShowCreditGlass(true);
    if (prize) firePackConfettiBurst(prize);
    packAfter(() => {
      resetPackOverlay();
    }, 1400);
  }, [claiming, revealPrize, claimPackRemote, firePackConfettiBurst, packAfter, resetPackOverlay]);

  const startPackOpen = useCallback(async () => {
    if (packOpening) return;
    const cred = Math.max(0, Math.floor(Number(freePacks) || 0));
    const useFree = cred > 0;
    if (!useFree && globalCoins < PACK_PRICE) {
      toast.error('Pas assez de Global Coins (50 requis)');
      return;
    }
    const balanceSnapshot = { coins: globalCoins, free: freePacks };
    setPackOpening(true);
    setShowCreditGlass(false);
    if (useFree) setFreePacks((n) => Math.max(0, n - 1));
    else setGlobalCoins((c) => Math.max(0, c - PACK_PRICE));

    const prizeId = await openPackRemote(useFree);
    if (!prizeId) {
      setGlobalCoins(balanceSnapshot.coins);
      setFreePacks(balanceSnapshot.free);
      setPackOpening(false);
      return;
    }

    const prize = { ...getPackPrizeMeta(prizeId) };
    pendingPrizeIdRef.current = prizeId;

    const box = packAnimBoxRef.current;
    const card = packRevealCardRef.current;
    const stack = packAnimStackRef.current;
    const slot = packCardSlotRef.current;
    if (!box || !card) {
      setPackOpening(false);
      return;
    }

    if (slot) slot.classList.remove('slot-released');
    if (stack) stack.classList.remove('pack-card-forward');
    card.style.display = 'none';
    card.classList.remove('reveal-play', 'reveal-in-front', 'pack-card-show-front', 'pack-card-do-settle');
    card.className = revealCardClassName(prize);
    setRevealPrize(prize);
    box.classList.remove('phase-tear-top', 'phase-rip-open', 'phase-tear', 'pack-shake-go');
    resetPackTopLip(box);
    void box.offsetWidth;
    setOvOpen(true);
    setOvBtnShow(false);
    spawnPackParticles();
    box.classList.add('pack-shake-go');

    const motionReduce = typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const PACK_SHAKE_MS = motionReduce ? 380 : 720;
    const PRIZE_REVEAL_MS = motionReduce ? 980 : 5300;
    const RECUP_BTN_MS = motionReduce ? 1180 : 5520;
    clearPackOpenTimers();
    packRevealDoneRef.current = false;

    const finishPackRevealDepth = () => {
      if (packRevealDoneRef.current) return;
      packRevealDoneRef.current = true;
      requestAnimationFrame(() => {
        if (slot && !slot.classList.contains('slot-released')) slot.classList.add('slot-released');
        card.classList.add('reveal-in-front');
        if (stack && !stack.classList.contains('pack-card-forward')) stack.classList.add('pack-card-forward');
        card.classList.add('pack-card-show-front');
      });
    };

    packAfter(() => {
      box.classList.remove('pack-shake-go');
      box.classList.add('phase-tear-top');
      packAfter(() => box.classList.add('phase-rip-open'), motionReduce ? 120 : 280);
      card.style.display = 'flex';
      void card.offsetWidth;
      const onBoosterReducedEnd = (e) => {
        if (!e || e.target !== card) return;
        const n = String(e.animationName || '');
        if (n !== 'packCardRevealReducedBooster' && n !== 'packCardRevealReduced') return;
        card.removeEventListener('animationend', onBoosterReducedEnd);
        finishPackRevealDepth();
      };
      const onSlideEndChain = (e) => {
        if (!e || e.target !== card) return;
        if (String(e.animationName || '') !== 'packCardRevealCinemaBooster') return;
        card.removeEventListener('animationend', onSlideEndChain);
        if (slot) slot.classList.add('slot-released');
        if (stack) stack.classList.add('pack-card-forward');
        card.classList.add('pack-card-do-settle', 'pack-card-show-front');
        card.addEventListener('animationend', onSettleEndOnce, { once: true });
      };
      const onSettleEndOnce = (e) => {
        if (!e || e.target !== card) return;
        if (String(e.animationName || '') !== 'packCardRevealSettle') return;
        card.removeEventListener('animationend', onSlideEndChain);
        card.removeEventListener('animationend', onSettleEndOnce);
        finishPackRevealDepth();
      };
      if (motionReduce) {
        card.addEventListener('animationend', onBoosterReducedEnd);
        card.classList.add('reveal-play');
        packAfter(finishPackRevealDepth, 900);
      } else {
        card.addEventListener('animationend', onSlideEndChain);
        card.classList.add('reveal-play');
        packAfter(() => {
          if (!packRevealDoneRef.current) finishPackRevealDepth();
        }, 5200);
      }
    }, PACK_SHAKE_MS);

    packAfter(() => {
      firePackConfettiBurst(prize);
    }, PRIZE_REVEAL_MS);

    packAfter(() => setOvBtnShow(true), RECUP_BTN_MS);
  }, [
    packOpening,
    freePacks,
    globalCoins,
    setFreePacks,
    setGlobalCoins,
    resetPackTopLip,
    spawnPackParticles,
    packAfter,
    clearPackOpenTimers,
    openPackRemote,
    firePackConfettiBurst,
  ]);

  const canOpen = freePacks > 0 || globalCoins >= PACK_PRICE;
  const openBtnLabel = freePacks > 0 ? (
    <span className="pack-open-btn-inner pack-open-btn-inner--free">OUVRIR</span>
  ) : (
    <span className="pack-open-btn-inner">
      <span className="pack-open-btn__num">{PACK_PRICE}</span>
      <CoinImg size={24} />
    </span>
  );

  return (
    <>
      <PackSwissBackground />
      {/* Carte + aperçu masqués dès que l’overlay d’ouverture est devant */}
      <div
        className={'global-pack-view with-app-mobile-nav' + (ovOpen ? ' global-pack-view--hidden' : '')}
        aria-hidden={ovOpen}
      >
        <button type="button" onClick={onBack} className="gs-pack-btn gs-pack-btn--back">
          <ChevronLeft size={20} />
          <span>Retour</span>
        </button>
        <div className="global-pack-hero">
          <h2>GLOBAL PACK</h2>
          <p>Ouvre un Global Pack et decouvre ta recompense.</p>
        </div>
        <PackReferralBanner freePacks={freePacks} onGoReferral={onGoReferral} />
        <div className="pack-stage-wrap">
          <div className="pack-anim-stack pack-anim-stack--idle">
            <div className="pack-anim-box pack-booster pack-booster--idle">
              <PackBoosterFace />
            </div>
          </div>
        </div>
        <div className="pack-open-row">
          <button
            type="button"
            className="gs-pack-btn gs-pack-btn--primary pack-open-btn"
            onClick={startPackOpen}
            disabled={packOpening || !canOpen}
          >
            {openBtnLabel}
          </button>
        </div>
        {freePacks > 0 && (
          <p className="global-pack-free-hint">
            {freePacks} Global Pack{freePacks > 1 ? 's' : ''} gratuit{freePacks > 1 ? 's' : ''} disponible{freePacks > 1 ? 's' : ''}
          </p>
        )}
      </div>

      <div className={'pack-ov' + (ovOpen ? ' on' : '')} ref={packOvRef} aria-hidden={!ovOpen}>
        <PackSwissBackground overlay />
        <div className="pack-ov-glow" />
        <div className="pack-ov-particles" ref={packOvParticlesRef} />
        <div className="pack-anim-stage">
          <div className="pack-anim-stack" ref={packAnimStackRef}>
            <div className="pack-anim-box pack-booster" ref={packAnimBoxRef}>
              <PackBoosterFace />
              <div className="pack-card-slot" ref={packCardSlotRef}>
                <div
                  ref={packRevealCardRef}
                  className={revealPrize ? revealCardClassName(revealPrize) : 'pack-reveal-card'}
                  style={{ display: revealPrize ? 'flex' : 'none' }}
                >
                  {revealPrize ? <RevealCard prize={revealPrize} /> : null}
                </div>
              </div>
            </div>
          </div>
        </div>
        <canvas ref={packConfettiCanvasRef} className="pack-confetti-canvas" aria-hidden="true" />
        <PackCreditGlass prize={revealPrize} visible={showCreditGlass} />
        <button
          type="button"
          className={'gs-pack-btn gs-pack-btn--primary pack-ov-close' + (ovBtnShow ? ' show' : '')}
          onClick={closePackOv}
          disabled={claiming}
        >
          {claiming ? 'CREDIT…' : 'RECUPERER'}
        </button>
      </div>
    </>
  );
}

